<script setup lang="ts">
import { computed, ref } from 'vue'

import clsx from 'clsx'

import api from '@/services/api'
import { useEditorPageStore } from '@/stores/editor/page'
import { useSiteStore } from '@/stores/site'

const editorPageStore = useEditorPageStore()
const siteStore = useSiteStore()

const loadingStore = ref(false)
const isHomePage = computed(() => Boolean(editorPageStore.activePage?.isHome))

const goStore = async () => {
    loadingStore.value = true
    const storeWindow = window.open('about:blank', '_blank')

    if (storeWindow) {
        storeWindow.opener = null
    }

    try {
        const response = await api.get(`store/login?site=${siteStore.id}`)
        const { status, url } = await response.json()

        if (status !== '1') {
            throw new Error('Erro ao buscar url da loja')
        }

        if (storeWindow) {
            storeWindow.location.href = url
        } else {
            window.open(url, '_blank', 'noopener,noreferrer')
        }
    } catch (error) {
        storeWindow?.close()
        console.error('error', error)
    }

    loadingStore.value = false
}
</script>

<template>
    <div
        :class="clsx(
            'beta-store-preview',
            isHomePage ? 'h-[30vh]' : 'h-[78vh]',
        )"
    >
        <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#beta-store-clip)">
                <mask id="beta-store-mask-0" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="120" height="120">
                    <path d="M120 0H0V120H120V0Z" fill="white" />
                </mask>
                <g mask="url(#beta-store-mask-0)">
                    <mask id="beta-store-mask-1" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="0" width="120" height="120">
                        <path d="M0 7.62939e-06H120V120H0V7.62939e-06Z" fill="white" />
                    </mask>
                    <g mask="url(#beta-store-mask-1)">
                        <path d="M76.8475 93.7494L99.7284 71.2042C102.72 68.2149 104.208 64.0381 103.779 59.8306L99.7694 20.4646L60.3007 16.453C56.0925 16.0253 51.9152 17.5145 48.9267 20.5082L8.79794 60.5042C3.31567 65.9956 3.31755 74.8899 8.80239 80.379L39.5868 111.189C45.0761 116.683 53.9798 116.817 59.4738 111.328" stroke="url(#beta-store-paint-0)" stroke-width="7.65625" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M83.3896 36.7964C83.3896 36.7964 115.326 35.1164 115.312 11.4075V11.2209C115.312 7.61225 112.387 4.68702 108.778 4.68702C108.778 4.68702 91.8747 6.09327 87.431 18.0464" stroke="url(#beta-store-paint-1)" stroke-width="7.65625" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M41.5146 79.5722L37.2656 83.6719" stroke="url(#beta-store-paint-2)" stroke-width="7.65625" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M72.4218 48.5151L67.8506 53.2358" stroke="url(#beta-store-paint-3)" stroke-width="7.65625" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M69.3201 65.3616C72.0599 62.6218 71.8494 58.2402 69.1136 55.5043L66.2413 52.6323C62.913 49.3037 57.6491 49.2078 54.3156 52.5413L53.5755 53.2815C50.2422 56.615 50.1236 62.0933 53.4519 65.4216L56.4892 68.4591C59.8178 71.7875 59.9099 77.0921 56.5764 80.4256L56.2314 80.7704C52.8981 84.1039 47.6912 83.9141 44.3627 80.5857L39.4265 75.6495C36.6906 72.9137 36.5392 68.6298 39.2791 65.8897" stroke="url(#beta-store-paint-4)" stroke-width="7.65625" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                    </g>
                </g>
            </g>
            <defs>
                <linearGradient id="beta-store-paint-0" x1="-35.7457" y1="65.8791" x2="105.616" y2="65.8791" gradientUnits="userSpaceOnUse">
                    <stop stop-color="var(--main-color)" />
                    <stop offset="1" stop-color="#8B51FF" />
                </linearGradient>
                <linearGradient id="beta-store-paint-1" x1="70.3736" y1="20.7417" x2="115.88" y2="20.7417" gradientUnits="userSpaceOnUse">
                    <stop stop-color="var(--main-color)" />
                    <stop offset="1" stop-color="#8B51FF" />
                </linearGradient>
                <linearGradient id="beta-store-paint-2" x1="35.5331" y1="81.622" x2="41.5902" y2="81.622" gradientUnits="userSpaceOnUse">
                    <stop stop-color="var(--main-color)" />
                    <stop offset="1" stop-color="#8B51FF" />
                </linearGradient>
                <linearGradient id="beta-store-paint-3" x1="65.9867" y1="50.8754" x2="72.5032" y2="50.8754" gradientUnits="userSpaceOnUse">
                    <stop stop-color="var(--main-color)" />
                    <stop offset="1" stop-color="#8B51FF" />
                </linearGradient>
                <linearGradient id="beta-store-paint-4" x1="23.4448" y1="66.6331" x2="71.8777" y2="66.6331" gradientUnits="userSpaceOnUse">
                    <stop stop-color="var(--main-color)" />
                    <stop offset="1" stop-color="#8B51FF" />
                </linearGradient>
                <clipPath id="beta-store-clip">
                    <rect width="120" height="120" fill="white" />
                </clipPath>
            </defs>
        </svg>

        <h2 class="text-[40px] font-bold">
            <span class="text-[var(--main-color)]">Parabéns! </span> Sua loja está online {{ ';)' }}
        </h2>

        <p class="text-lg">
            Para gerenciar sua EPICS Store, acesse o seu
            <span class="text-[var(--main-color)]">painel de gerenciamento clicando no botão abaixo.</span>
        </p>

        <button
            type="button"
            @click.stop="goStore"
            :disabled="loadingStore"
            class="mt-8 flex w-60 items-center justify-center rounded-full border-2 border-[var(--main-color)] bg-transparent px-6 py-3 text-[var(--main-color)] disabled:cursor-wait"
        >
            <span v-if="!loadingStore">
                Acessar meu painel
            </span>

            <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-6 w-6 animate-spin stroke-[var(--main-color)]">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
            </svg>
        </button>
    </div>
</template>

<style scoped>
.beta-store-preview {
    @apply flex w-full flex-col items-center justify-center text-center;
}
</style>
